import { Avatar, AvatarFallback, AvatarImage, AvatarInitials } from "@/components/Avatar";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { SearchInput } from "@/components/flexkit/SearchInput";
import { Checkbox } from "@/components/flexkit/checkbox/Checkbox";
import { Pill } from "@/components/podkit/status/Pill";
import { useMembers } from "@/queries/organization-queries";
import { useListServiceAccounts } from "@/queries/service-account-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { useState, useMemo, useCallback, type FC } from "react";
import { UserStatus } from "gitpod-next-api/gitpod/v1/user_pb";
import { Principal } from "gitpod-next-api/gitpod/v1/identity_pb";
import { ServiceAccountAvatar } from "@/components/settings/organization/service-accounts/ServiceAccountAvatar";
import type { Subject, SelectableSubject } from "@/components/settings/organization/groups/subject-types";

export type { Subject, SelectableSubject } from "@/components/settings/organization/groups/subject-types";

type MemberSelectionListProps = {
    excludeSubjectIds?: Set<string>;
    selectedSubjects?: Map<string, Subject>;
    onChangeSelection: (selectedSubjects: Map<string, Subject>) => void;
    showSearch?: boolean;
    showSelectedCount?: boolean;
    className?: string;
};

export const MemberSelectionList: FC<MemberSelectionListProps> = ({
    excludeSubjectIds,
    selectedSubjects,
    onChangeSelection,
    showSearch = true,
    showSelectedCount = true,
    className,
}) => {
    const effectiveExcludeIds = excludeSubjectIds;
    const effectiveSelectedSubjects = useMemo(
        () => selectedSubjects ?? new Map<string, Subject>(),
        [selectedSubjects],
    );

    const { data: user } = useAuthenticatedUser();
    const { data: membersData } = useMembers();
    const { data: serviceAccountsData } = useListServiceAccounts();
    const [searchValue, setSearchValue] = useState("");

    // Combine users and service accounts into a unified list
    const availableSubjects = useMemo(() => {
        const subjects: SelectableSubject[] = [];

        // Add organization members (users)
        if (membersData?.members) {
            for (const member of membersData.members) {
                if (member.userId && member.status === UserStatus.ACTIVE && !effectiveExcludeIds?.has(member.userId)) {
                    const loginProvider = (() => {
                        switch (member.loginProvider) {
                            case "magiclink":
                                return "Email";
                            case "github":
                                return "GitHub";
                            case "gitlab":
                                return "GitLab";
                            case "google":
                                return "Google";
                            case "custom":
                                return "SSO";
                            default:
                                return "";
                        }
                    })();
                    subjects.push({
                        id: member.userId,
                        principal: Principal.USER,
                        name: member.fullName || "",
                        avatarUrl: member.avatarUrl,
                        secondaryText: loginProvider,
                        email: member.email,
                        isCurrentUser: member.userId === user?.id,
                    });
                }
            }
        }

        // Add service accounts
        if (serviceAccountsData?.serviceAccounts) {
            for (const sa of serviceAccountsData.serviceAccounts) {
                if (sa.id && !effectiveExcludeIds?.has(sa.id)) {
                    subjects.push({
                        id: sa.id,
                        principal: Principal.SERVICE_ACCOUNT,
                        name: sa.name || "",
                        secondaryText: "Service Account",
                    });
                }
            }
        }

        // Sort alphabetically by name
        return subjects.sort((a, b) => a.name.localeCompare(b.name));
    }, [membersData?.members, serviceAccountsData?.serviceAccounts, effectiveExcludeIds, user?.id]);

    // Filter subjects by search
    const filteredSubjects = useMemo(() => {
        if (!searchValue.trim()) return availableSubjects;

        const query = searchValue.trim().toLowerCase();
        return availableSubjects.filter(
            (subject) =>
                subject.name.toLowerCase().includes(query) ||
                subject.secondaryText?.toLowerCase().includes(query) ||
                subject.email?.toLowerCase().includes(query),
        );
    }, [availableSubjects, searchValue]);

    const selectedCount = effectiveSelectedSubjects.size;

    const handleSelectionChange = useCallback(
        (subject: SelectableSubject, checked: boolean) => {
            const newMap = new Map(effectiveSelectedSubjects);
            if (checked) {
                newMap.set(subject.id, { id: subject.id, principal: subject.principal });
            } else {
                newMap.delete(subject.id);
            }
            onChangeSelection(newMap);
        },
        [effectiveSelectedSubjects, onChangeSelection],
    );

    return (
        <div className={className}>
            {/* Search Input */}
            {showSearch && (
                <SearchInput
                    wrapperClassName="w-full"
                    className="max-w-none"
                    placeholder="Search by name or keyword"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onClear={() => setSearchValue("")}
                    data-tracking-id="member-selection-search"
                />
            )}

            {/* Subjects List */}
            <ScrollArea orientation="vertical" className="min-h-0 flex-1 rounded-lg border border-border-base">
                {filteredSubjects.length === 0 ? (
                    <div className="p-4 text-center text-content-secondary">
                        {searchValue.trim()
                            ? "No members found matching your search."
                            : "No available members to add."}
                    </div>
                ) : (
                    <div className="divide-y divide-border-base">
                        {filteredSubjects.map((subject) => {
                            const isSelected = effectiveSelectedSubjects.has(subject.id);
                            const isServiceAccount = subject.principal === Principal.SERVICE_ACCOUNT;
                            return (
                                <label
                                    key={subject.id}
                                    htmlFor={`member-checkbox-${subject.id}`}
                                    className="ml-2 flex cursor-pointer items-center gap-2 px-2 py-2 hover:bg-surface-secondary"
                                    data-tracking-id="member-selection-row"
                                >
                                    <Checkbox
                                        id={`member-checkbox-${subject.id}`}
                                        checked={isSelected}
                                        onCheckedChange={(checked) => handleSelectionChange(subject, !!checked)}
                                        data-testid={`member-checkbox-${subject.id}`}
                                        data-tracking-id="member-selection-checkbox"
                                    />
                                    {isServiceAccount ? (
                                        <ServiceAccountAvatar
                                            id={subject.id}
                                            size={24}
                                            className="size-6 flex-shrink-0"
                                        />
                                    ) : (
                                        <Avatar
                                            size={24}
                                            className="size-6 flex-shrink-0 rounded-full border-2 border-surface-glass bg-surface-glass ring-1 ring-border-light"
                                        >
                                            {subject.avatarUrl && (
                                                <AvatarImage src={subject.avatarUrl} alt={`${subject.name}'s avatar`} />
                                            )}
                                            <AvatarFallback>
                                                <AvatarInitials name={subject.name} size={24} />
                                            </AvatarFallback>
                                        </Avatar>
                                    )}
                                    <div className="flex min-w-0 flex-1 items-center gap-2">
                                        <p className="truncate font-medium text-content-primary">{subject.name}</p>
                                        {subject.isCurrentUser && <Pill variant="success">me</Pill>}
                                        {subject.secondaryText && (
                                            <span className="text-sm text-content-secondary">
                                                {subject.secondaryText}
                                            </span>
                                        )}
                                    </div>
                                </label>
                            );
                        })}
                    </div>
                )}
            </ScrollArea>

            {showSelectedCount && (
                <p className="flex-shrink-0 text-sm text-content-secondary" style={{ minHeight: "1.25rem" }}>
                    {selectedCount > 0 && `${selectedCount} ${selectedCount === 1 ? "member" : "members"} selected`}
                </p>
            )}
        </div>
    );
};
